import { toggleTheme } from './styles';

export interface BrandConfig {
  name: string;
  subtitle: string;
  icon: string;
}

export interface NavItem {
  id: string;
  label: string;
  icon: string;
  hash: string;
  hasBadge?: boolean;
}

export interface ShellConfig {
  brand: BrandConfig;
  navItems: NavItem[];
  showClock?: boolean;
  showThemeToggle?: boolean;
  statusText?: string;
}

export interface Shell {
  content: HTMLElement;
  updateNavHighlight: (activeId: string) => void;
  updateBadge: (navId: string, count: number) => void;
}

const SUN_ICON = '<svg class="w-4 h-4 hidden dark:block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>';
const MOON_ICON = '<svg class="w-4 h-4 dark:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/></svg>';

export function createShell(config: ShellConfig): Shell {
  const app = document.getElementById('app')!;
  app.innerHTML = '';

  const showToggle = config.showThemeToggle !== false;

  const sidebar = document.createElement('aside');
  sidebar.className = 'w-64 sidebar-bg border-r border-th-border flex flex-col shrink-0 h-full';
  sidebar.innerHTML = `
    <div class="px-5 py-6 border-b border-th-border">
      <div class="flex items-center gap-3">
        <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
          ${config.brand.icon}
        </div>
        <div>
          <span class="text-base font-bold text-th-text tracking-tight">${config.brand.name}</span>
          <p class="text-[10px] text-th-text-3 uppercase tracking-[0.2em] font-medium">${config.brand.subtitle}</p>
        </div>
      </div>
    </div>
    <nav class="flex-1 px-3 py-5 space-y-1" id="nav-list"></nav>
    <div class="px-5 py-4 border-t border-th-border">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div class="live-dot"></div>
          <span class="text-xs text-th-text-3 font-medium">${config.statusText ?? 'System Online'}</span>
        </div>
        <div class="flex items-center gap-2">
          ${showToggle ? `<button id="theme-toggle" class="p-1.5 rounded-lg text-th-text-3 hover:text-th-text hover:bg-th-surface transition-colors" title="Toggle theme">${SUN_ICON}${MOON_ICON}</button>` : ''}
          ${config.showClock !== false ? '<span class="text-[10px] text-th-text-4 font-mono" id="sidebar-clock"></span>' : ''}
        </div>
      </div>
    </div>
  `;

  const content = document.createElement('main');
  content.className = 'flex-1 h-full overflow-hidden bg-th-bg';
  content.id = 'content';

  const mobileNav = document.createElement('nav');
  mobileNav.id = 'mobile-nav';
  mobileNav.className = 'shrink-0 h-14 bg-th-bg border-t border-th-border flex items-center justify-around px-2';
  config.navItems.forEach(item => {
    const link = document.createElement('a');
    link.href = item.hash;
    link.dataset.mobilenav = item.id;
    link.className = 'mobile-nav-link flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-th-text-3 transition-colors relative';
    if (item.hasBadge) {
      link.innerHTML = `<span class="relative">${item.icon}<span data-badge-mobile="${item.id}" class="hidden absolute -top-1 -right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold rounded-full bg-red-500 text-white"></span></span><span class="text-[10px] font-medium">${item.label}</span>`;
    } else {
      link.innerHTML = `${item.icon}<span class="text-[10px] font-medium">${item.label}</span>`;
    }
    mobileNav.appendChild(link);
  });

  app.appendChild(sidebar);
  app.appendChild(content);
  app.appendChild(mobileNav);

  const navList = sidebar.querySelector('#nav-list')!;
  config.navItems.forEach(item => {
    const link = document.createElement('a');
    link.href = item.hash;
    link.dataset.nav = item.id;
    link.className = 'nav-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200';
    if (item.hasBadge) {
      link.innerHTML = `${item.icon}<span class="flex-1">${item.label}</span><span data-badge-nav="${item.id}" class="hidden min-w-[20px] h-5 px-1.5 flex items-center justify-center text-[10px] font-bold rounded-full bg-red-500 text-white badge-glow"></span>`;
    } else {
      link.innerHTML = `${item.icon}<span>${item.label}</span>`;
    }
    navList.appendChild(link);
  });

  if (showToggle) {
    sidebar.querySelector('#theme-toggle')?.addEventListener('click', () => {
      toggleTheme();
    });
  }

  if (config.showClock !== false) {
    function updateClock() {
      const el = document.getElementById('sidebar-clock');
      if (el) el.textContent = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
    updateClock();
    setInterval(updateClock, 30000);
  }

  function updateNavHighlight(activeId: string) {
    document.querySelectorAll('.nav-link').forEach(el => {
      const link = el as HTMLElement;
      if (link.dataset.nav === activeId) {
        link.className = 'nav-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium nav-link-active text-blue-400';
      } else {
        link.className = 'nav-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-th-text-3 hover:text-th-text hover:bg-th-surface transition-all duration-200';
      }
    });
    document.querySelectorAll('.mobile-nav-link').forEach(el => {
      const link = el as HTMLElement;
      if (link.dataset.mobilenav === activeId) {
        link.className = 'mobile-nav-link flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-blue-400 transition-colors relative';
      } else {
        link.className = 'mobile-nav-link flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-th-text-3 transition-colors relative';
      }
    });
  }

  function updateBadge(navId: string, count: number) {
    const navBadge = document.querySelector(`[data-badge-nav="${navId}"]`) as HTMLElement | null;
    const mobileBadge = document.querySelector(`[data-badge-mobile="${navId}"]`) as HTMLElement | null;
    [navBadge, mobileBadge].forEach(badge => {
      if (!badge) return;
      if (count > 0) {
        badge.textContent = count > 99 ? '99+' : String(count);
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    });
  }

  return { content, updateNavHighlight, updateBadge };
}

export interface Route {
  pattern: string;
  render: (container: HTMLElement, params: Record<string, string>) => Promise<(() => void) | void>;
}

export function createRouter(routes: Route[], shell: Shell, getActiveNav: (hash: string) => string) {
  let currentCleanup: (() => void) | null = null;

  async function route() {
    const hash = window.location.hash || '#/';
    const content = shell.content;

    if (currentCleanup) {
      currentCleanup();
      currentCleanup = null;
    }

    document.querySelectorAll('[data-modal-overlay]').forEach(el => el.remove());

    shell.updateNavHighlight(getActiveNav(hash));

    content.innerHTML = '<div class="flex items-center justify-center h-full"><div class="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div></div>';

    const match = matchRoute(routes, hash);
    if (match) {
      try {
        const cleanup = await match.route.render(content, match.params);
        if (typeof cleanup === 'function') currentCleanup = cleanup;
      } catch (err) {
        content.innerHTML = '<div class="flex items-center justify-center h-full text-red-400">Error loading page</div>';
        console.error(err);
      }
    } else {
      content.innerHTML = '<div class="flex items-center justify-center h-full text-th-text-3">Page not found</div>';
    }
  }

  if (!window.location.hash) window.location.hash = '#/';
  route();
  window.addEventListener('hashchange', route);
}

function matchRoute(routes: Route[], hash: string): { route: Route; params: Record<string, string> } | null {
  for (const route of routes) {
    const params = matchPattern(route.pattern, hash);
    if (params !== null) return { route, params };
  }
  return null;
}

function matchPattern(pattern: string, hash: string): Record<string, string> | null {
  const patternParts = pattern.split('/');
  const hashParts = hash.split('/');

  if (patternParts.length !== hashParts.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(':')) {
      params[patternParts[i].slice(1)] = hashParts[i];
    } else if (patternParts[i] !== hashParts[i]) {
      return null;
    }
  }
  return params;
}

export function navigate(hash: string) {
  window.location.hash = hash;
}
